import { useEffect, useState } from "react";
import { Pencil, Plus, Receipt } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EmptyState, Field, PageHeader } from "@/components/common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { NumInput } from "@/components/NumInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useApi } from "@/hooks/useApi";
import { api, brl, errMsg, EXPENSE_CATEGORIES } from "@/lib/api";
import { FinancialCosts } from "@/components/FinancialCosts";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const EMPTY = { name: "", category: "Ocupação", amount: "" };

const ExpenseDialog = ({ open, onOpenChange, item, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(item ? { ...item } : EMPTY); }, [item, open]);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = { name: f.name, category: f.category, amount: Number(f.amount) || 0 };
      item?.id ? await api.put(`/expenses/${item.id}`, body) : await api.post("/expenses", body);
      toast.success("Despesa salva"); onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="font-display">{item?.id ? "Editar despesa" : "Nova despesa fixa"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Descrição"><Input data-testid="expense-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Aluguel, Energia, Internet..." /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Categoria">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                <SelectContent>{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c} data-testid={`expense-category-${c}`}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Valor mensal (R$)"><NumInput data-testid="expense-amount" min="0" step="0.01" required value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></Field>
          </div>
          <Button data-testid="expense-save" type="submit" className="w-full" disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Expenses() {
  const { data, loading, reload } = useApi("/expenses");
  const [dlg, setDlg] = useState({ open: false, item: null });
  const list = data?.expenses || [];
  const remove = async (id) => { try { await api.delete(`/expenses/${id}`); toast.success("Removida"); reload(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div>
      <PageHeader testId="expenses-page" title="Despesas" subtitle="Despesas fixas do mês e custos financeiros (empréstimos, financiamentos, reformas)."
        help="Despesas fixas são as contas que chegam todo mês mesmo que você venda zero: aluguel, energia, água, internet, contador, sistema. Elas não entram no custo do produto — são pagas pela margem de contribuição e aparecem no DRE antes do lucro operacional. Custos financeiros (aba ao lado) são parcelas de empréstimos, financiamentos e juros: ficam separados e entram no DRE depois do lucro operacional, para você enxergar o peso das dívidas no lucro líquido." helpTitle="Despesas fixas × custos financeiros"
        actions={<Button onClick={() => setDlg({ open: true, item: null })} data-testid="expenses-new-btn"><Plus className="w-4 h-4 mr-2" />Nova despesa fixa</Button>} />

      <Tabs defaultValue="fixas">
        <TabsList className="mb-6"><TabsTrigger value="fixas" data-testid="expenses-tab-fixed">Despesas fixas</TabsTrigger><TabsTrigger value="financeiros" data-testid="expenses-tab-financial">Custos financeiros</TabsTrigger></TabsList>
        <TabsContent value="financeiros"><FinancialCosts /></TabsContent>
        <TabsContent value="fixas">
      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-4">
          <div className="rounded-2xl border border-border bg-card p-6 rise rise-1">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Total mensal</p>
            <p className="num text-4xl font-extrabold text-primary mt-2" data-testid="expenses-total">{brl(data?.total)}</p>
            <p className="text-xs text-muted-foreground mt-1">{list.length} despesa(s) recorrente(s)</p>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 space-y-3 rise rise-2">
            <p className="font-display font-semibold">Por categoria</p>
            {(data?.by_category || []).map((c) => (
              <div key={c.name} data-testid={`expense-cat-${c.name}`}>
                <div className="flex justify-between text-sm"><span>{c.name}</span><span className="num">{brl(c.value)}</span></div>
                <div className="h-1.5 rounded-full bg-secondary mt-1 overflow-hidden"><div className="h-full bg-primary rounded-full transition-all" style={{ width: `${data.total ? (c.value / data.total) * 100 : 0}%` }} /></div>
              </div>
            ))}
            {!data?.by_category?.length && <p className="text-sm text-muted-foreground">Sem despesas ainda.</p>}
          </div>
        </div>
        <div className="lg:col-span-8">
          {!loading && !list.length ? (
            <EmptyState testId="expenses-empty" icon={Receipt} title="Nenhuma despesa cadastrada" text="Comece pelo aluguel, energia e internet — as mais comuns."
              action={<Button onClick={() => setDlg({ open: true, item: null })} data-testid="expenses-empty-new">Cadastrar despesa</Button>} />
          ) : (
            <div className="rounded-2xl border border-border bg-card overflow-hidden rise rise-2">
              <Table>
                <TableHeader><TableRow><TableHead>Despesa</TableHead><TableHead>Categoria</TableHead><TableHead className="text-right">Valor/mês</TableHead><TableHead className="w-24"></TableHead></TableRow></TableHeader>
                <TableBody>
                  {list.map((x) => (
                    <TableRow key={x.id} data-testid={`expense-row-${x.id}`}>
                      <TableCell className="font-medium">{x.name}</TableCell>
                      <TableCell><Badge variant="secondary">{x.category}</Badge></TableCell>
                      <TableCell className="text-right num font-semibold">{brl(x.amount)}</TableCell>
                      <TableCell><div className="flex justify-end"><Button variant="ghost" size="icon" data-testid={`expense-edit-${x.id}`} onClick={() => setDlg({ open: true, item: x })} aria-label="Editar"><Pencil className="w-4 h-4" /></Button><ConfirmDelete what={`"${x.name}"`} onConfirm={() => remove(x.id)} testId={`expense-delete-${x.id}`} /></div></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
        </TabsContent>
      </Tabs>
      <ExpenseDialog open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} item={dlg.item} onSaved={reload} />
    </div>
  );
}
